import { ColorInput } from '@mantine/core';
import c from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useToasts } from 'react-toast-notifications';
import { PageWizardProps } from '.';
import FeatureIconImage from '../../../common/FeatureIconImage';
import CodeEditor from '../../../components/CodeEditor';
import Error from '../../../components/Error';
import FileInput from '../../../components/FileInput';
import FontPicker from '../../../components/FontPicker';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../../components/Form';
import AutoSave from '../../../components/FormAutoSave';
import { Pill } from '../../../components/Pill';
import { ROUTES } from '../../../routes/routes';
import { PageDesignSettings, PageSettings } from '../../../types/pricingPage';
import { DEFAULT_PRICING_CUSTOM_CSS } from '../../../utils/constants';
import { BORDER_RADIUS_OPTIONS, FEATURE_ICONS, SHADOW_OPTIONS, SUBDUED_ICONS } from '../../../utils/features';
import { useAxios } from '../../../utils/hooks';
import { isStripePrice } from '../../../utils/products';
import Footer from './Footer';
import PageSettingsForm from './PageSettingsForm';

const CUSTOM_ICON_PREFIX = 'feature-icon';

interface Props extends PageWizardProps {
    onChangeDesignSettings: (designSettings: PageDesignSettings) => Promise<void>;
    onChangeSettings: (settings: PageSettings) => void;
}

export default function ChooseDesign({ onChangeDesignSettings, page, onChangeSettings }: Props) {
    const axios = useAxios();
    const { addToast } = useToasts();
    const { designSettings } = page;
    const [showCustomCss, setShowCustomCss] = useState(false);
    const [customIcons, setCustomIcons] = useState<{ url: string }[]>([]);
    // FormAutoSave means we don't use the form submission process so store errors manually here
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [fileIsUploading, setFileIsUploading] = useState(false);

    const uploadCustomIcon = useCallback(
        async (file: File) => {
            if (!axios.current) {
                return;
            }
            setFileIsUploading(true);
            const formData = new FormData();
            formData.append('file', file);
            try {
                const res = await axios.current.post<{ url: string }>(`/custom-files/${CUSTOM_ICON_PREFIX}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                setCustomIcons([...customIcons, { url: res.data.url }]);
            } catch (err: any) {
                addToast(err?.response?.data?.error ?? 'Something went wrong. Please reload the page and try again.', {
                    appearance: 'error',
                });
            } finally {
                setFileIsUploading(false);
            }
        },
        [axios, customIcons, addToast]
    );
    useEffect(() => {
        axios.current?.get<{ url: string }[]>(`/custom-files/${CUSTOM_ICON_PREFIX}`).then(
            (res) => setCustomIcons(res.data),
            (err) => {
                addToast(err?.response?.data?.error ?? 'Something went wrong. Please reload the page and try again.', {
                    appearance: 'error',
                });
            }
        );
    }, [axios, addToast]);

    const handleUploadCustomIcon = async (files: File[]) => {
        files.forEach(uploadCustomIcon);
    };

    const handleToggleCustomCss = () => setShowCustomCss(!showCustomCss);

    const save = async (values: PageDesignSettings) => {
        setSubmitError(undefined);
        try {
            await onChangeDesignSettings(values);
        } catch (err) {
            setSubmitError('Error Saving.');
        }
    };

    return (
        <div>
            <div className="font-bold mb-4 text-center">Customize your Pricing Page</div>
            <Form<PageDesignSettings> onSubmit={save} initialValues={{ ...designSettings }}>
                {({ handleSubmit, form }) => (
                    <StyledForm onSubmit={handleSubmit}>
                        <FormSection>
                            <FormSectionHeader>Webpage</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Title</label>
                                    <Field name="companyName" type="text">
                                        {(props) => (
                                            <input
                                                {...props.input}
                                                className="input w-full"
                                                placeholder="My Company..."
                                            />
                                        )}
                                    </Field>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Layout</FormSectionHeader>
                            <FormSectionBody>
                                <label className="pb-4">Button Position</label>
                                <div className="pb-4 flex flex-col space-y-2">
                                    <label>
                                        <Field name="layout.button" component="input" type="radio" value="top" /> Top
                                    </label>
                                    <label>
                                        <Field name="layout.button" component="input" type="radio" value="bottom" />{' '}
                                        Bottom (default)
                                    </label>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Style</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label>Font</label>
                                    <Field name="font">
                                        {({ input: { onChange, ...inputProps } }) => (
                                            <FontPicker
                                                onChange={(fontFamily) =>
                                                    fontFamily ? onChange(fontFamily) : form.change('font', '')
                                                }
                                                {...inputProps}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label>Color</label>
                                    <Field name="backgroundColor">
                                        {(props) => (
                                            <label>
                                                <ColorInput
                                                    {...props.input}
                                                    size="md"
                                                    format="hex"
                                                    withPicker={false}
                                                    swatches={[
                                                        '#2e2e2e',
                                                        '#fa5252',
                                                        '#e64980',
                                                        '#be4bdb',
                                                        '#7950f2',
                                                        '#4c6ef5',
                                                        '#228be6',
                                                        '#15aabf',
                                                    ]}
                                                />
                                            </label>
                                        )}
                                    </Field>
                                </div>
                                <div className="my-4">
                                    <div>
                                        <Field name="animatePlanOnHover" type="checkbox">
                                            {(props) => (
                                                <label className="inline-flex items-center">
                                                    <input
                                                        {...props.input}
                                                        type="checkbox"
                                                        className="checkbox"
                                                        defaultValue="false"
                                                    />
                                                    <span className="ml-2">Animate plans on hover</span>
                                                </label>
                                            )}
                                        </Field>
                                    </div>
                                    <div>
                                        <Field name="disableFlexCol" type="checkbox">
                                            {(props) => (
                                                <label className="inline-flex items-center">
                                                    <input
                                                        {...props.input}
                                                        type="checkbox"
                                                        className="checkbox"
                                                        defaultValue="false"
                                                    />
                                                    <span className="ml-2">Responsive Mode: Fixed Height</span>
                                                </label>
                                            )}
                                        </Field>
                                    </div>
                                    <div>
                                        <Field name="hideProductImages" type="checkbox">
                                            {(props) => (
                                                <label className="inline-flex items-center">
                                                    <input
                                                        {...props.input}
                                                        type="checkbox"
                                                        className="checkbox"
                                                        defaultValue="false"
                                                    />
                                                    <span className="ml-2">Hide product images</span>
                                                </label>
                                            )}
                                        </Field>
                                    </div>
                                </div>
                                <div>
                                    <label>
                                        Custom CSS{' '}
                                        <button className="text-xs ml-2 underline" onClick={handleToggleCustomCss}>
                                            {showCustomCss ? 'Hide' : 'Show'} Custom CSS
                                        </button>
                                    </label>
                                    <Field name="customCSS" component="textarea" type="text">
                                        {({ input: { onChange, ...inputProps } }) => (
                                            <div className={c({ hidden: !showCustomCss })}>
                                                <CodeEditor
                                                    onChange={(code) =>
                                                        onChange(!code ? DEFAULT_PRICING_CUSTOM_CSS : code)
                                                    }
                                                    language="css"
                                                    {...inputProps}
                                                />
                                            </div>
                                        )}
                                    </Field>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Promoted Plan</FormSectionHeader>
                            <FormSectionBody>
                                {!page.settings.promotedPlan && (
                                    <div className="mb-2 font-light">
                                        <Pill colour="YELLOW" title="Warning" /> "Promoted Plan" is disabled. You can
                                        enable it in the page settings by clicking the cog.
                                    </div>
                                )}
                                <Field name="mostPopular.enabled" type="checkbox">
                                    {(props) => (
                                        <label className="inline-flex items-center">
                                            <input
                                                {...props.input}
                                                type="checkbox"
                                                className="checkbox"
                                                defaultValue="false"
                                            />
                                            <span className="ml-2">Show border</span>
                                        </label>
                                    )}
                                </Field>
                                <div className="mt-2">
                                    <label className="text-sm text-gray-600">Wording</label>
                                    <Field name="mostPopular.wording" type="text">
                                        {(props) => (
                                            <input {...props.input} className="input w-full" placeholder="Popular" />
                                        )}
                                    </Field>
                                </div>
                                <div className="mt-2">
                                    <label className="text-sm text-gray-600">Promoted Product</label>
                                    <Field name="mostPopular.stripeProductId" component="select">
                                        {(props) => (
                                            <select {...props.input} className="block pl-3 pr-10 select">
                                                <option value="">Default</option>
                                                {page.plans
                                                    .filter(
                                                        (plan) => isStripePrice(plan.product) && !!plan.stripeProductId
                                                    )
                                                    .map((plan) => (
                                                        <option value={plan.stripeProductId || ''} key={plan.id}>
                                                            {plan.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        )}
                                    </Field>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Buttons</FormSectionHeader>
                            <FormSectionBody>
                                <Field name="buttonGradient" type="checkbox">
                                    {(props) => (
                                        <label className="inline-flex items-center">
                                            <input
                                                {...props.input}
                                                type="checkbox"
                                                className="checkbox"
                                                defaultValue="false"
                                            />
                                            <span className="ml-2">Color Gradient</span>
                                            <span className="text-xs ml-2">(for promoted plan)</span>
                                        </label>
                                    )}
                                </Field>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Features</FormSectionHeader>
                            <FormSectionBody>
                                <label className="pb-4">Icon</label>
                                <div className="pb-4 grid grid-cols-6 gap-1">
                                    {FEATURE_ICONS.map((featureIcon) => (
                                        <label key={featureIcon.name}>
                                            <Field
                                                name="featureIcon"
                                                component="input"
                                                type="radio"
                                                value={featureIcon.name}
                                            />{' '}
                                            <FeatureIconImage featureIcon={featureIcon.name} />
                                        </label>
                                    ))}
                                    {customIcons.map((icon) => (
                                        <label key={icon.url}>
                                            <Field name="featureIcon" component="input" type="radio" value={icon.url} />{' '}
                                            <FeatureIconImage featureIcon={icon.url} />
                                        </label>
                                    ))}
                                    <FileInput
                                        label="Upload"
                                        accept="image/jpeg,image/png"
                                        onChange={handleUploadCustomIcon}
                                        busy={fileIsUploading}
                                    />
                                </div>
                                <label className="pb-4">Subdued Icon</label>
                                <div className="pb-4 grid grid-cols-6 gap-1">
                                    {SUBDUED_ICONS.map((featureIcon) => (
                                        <label key={featureIcon.name}>
                                            <Field
                                                name="subduedIcon"
                                                component="input"
                                                type="radio"
                                                value={featureIcon.name}
                                            />{' '}
                                            <FeatureIconImage featureIcon={featureIcon.name} />
                                        </label>
                                    ))}
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Plan</FormSectionHeader>
                            <FormSectionBody>
                                <div>
                                    <label>Corners</label>
                                    <Field name="borderRadius" component="select">
                                        {(props) => (
                                            <select {...props.input} className="block pl-3 pr-10 select">
                                                {BORDER_RADIUS_OPTIONS.map((option) => (
                                                    <option value={option.name} key={option.name}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </Field>
                                </div>
                                <div>
                                    <label>Shadow</label>
                                    <Field name="shadow" component="select">
                                        {(props) => (
                                            <select {...props.input} className="block pl-3 pr-10 select">
                                                {SHADOW_OPTIONS.map((option) => (
                                                    <option value={option.name} key={option.name}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </Field>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <div className="flex px-8">
                            <div className="mr-0 ml-auto">
                                {submitError && <Error message={submitError} />}
                                <AutoSave debounce={1000} save={save} />
                            </div>
                        </div>
                    </StyledForm>
                )}
            </Form>
            <PageSettingsForm page={page} onChangeSettings={onChangeSettings} />
            <Footer
                prev={{ href: ROUTES.PAGES_NEW_FEATURES, disabled: false }}
                next={{ href: ROUTES.PAGES_NEW_SNIPPET, disabled: false }}
                item={page}
            />
        </div>
    );
}
